/**
 * MIN-223 § Sound policy, as a pure function.
 *
 * Sound is a best-effort PRESENTATION effect, never a delivery guarantee — so
 * the rule that decides it is kept here, free of timers, stores and React, and
 * pinned by unit tests. Every condition below is a numbered line in the ticket;
 * the numbering is preserved so the two can be diffed by eye.
 *
 * The load-bearing idea: a Firestore hint is only ever a claim that something
 * changed. It carries no alert content (the contract forbids it), so it can
 * never authorise sound on its own. Canonical REST is what turns the claim into
 * a fact.
 */
import { AlertStatus } from "@/alerts/types/machine-alert";
import type { AlertLiveHint } from "@/alerts/stores/use-alert-live-store";
import type { AlertRow } from "@/alerts/types/alerts-workspace";

/**
 * The only fields the gate reads.
 *
 * Deliberately narrower than a queue row: a hint is confirmed against whatever
 * canonical REST just returned, and that includes HISTORY rows from the arrival
 * probe — the queue is oldest-first and page-capped, so the alert a hint names
 * is precisely the one not on it. `machine_id` is queue-only, hence optional;
 * the hint carries its own and is preferred anyway.
 */
export type SoundCandidateRow = Pick<
  AlertRow,
  "id" | "status" | "review_cycle" | "device_mxid" | "type"
> & { machine_id?: number | null };

/** The scope filters currently narrowing the operator's queue view. */
export interface SoundFilters {
  /** Empty means "no machine filter" — every machine passes. */
  machineIds?: number[];
  /** Empty means "no device filter". */
  deviceMxids?: string[];
  /** Empty means "no type filter". */
  types?: string[];
}

/** Why a hint was refused, for the operator-visible activity log. */
export type SoundRefusal =
  | "not_attention"
  | "not_in_queue_page"
  | "not_pending"
  | "stale_review_cycle"
  | "filtered_out";

export type SoundVerdict = { eligible: true } | { eligible: false; reason: SoundRefusal };

const matches = <T>(selected: T[] | undefined, value: T | null): boolean => {
  if (!selected || selected.length === 0) return true;
  // A null machine on an alert cannot satisfy an active machine filter — the
  // ticket calls this out explicitly ("machine_id = null while a machine filter
  // is active" is in the do-not-play list).
  if (value == null) return false;
  return selected.includes(value);
};

/**
 * Decide whether one post-baseline hint may sound, given what REST just
 * returned.
 *
 * `rows` MUST be freshly read, and MUST be only what is already in hand: walking
 * further cursor pages to hunt for the alert is explicitly out of scope, and an
 * alert absent from what we hold is a refusal, not a reason to go looking. That
 * bounds the work a single hint can cost. Do not "fix" that here.
 *
 * MIN-223 words condition 3 as "a fresh QUEUE response still contains the
 * Alert". Read literally it can never be satisfied — the queue is oldest-first
 * with a page cap, so on any real backlog the alert a hint names is precisely
 * the one not on it, and the whole Firestore path would be dead by construction.
 * The condition's purpose is that canonical REST confirms the alert exists, is
 * still Pending and is on the right review cycle; the arrival probe reads the
 * same canonical source, so rows from either satisfy it.
 *
 * Condition 2 (post-baseline) is not checked here: the listener never emits a
 * hint during the silent baseline, so by construction every hint reaching this
 * function has already satisfied it.
 */
export const soundVerdict = (
  hint: AlertLiveHint,
  rows: SoundCandidateRow[],
  filters: SoundFilters = {},
): SoundVerdict => {
  // (1) Only `attention` may sound. `cleared` and `info` are state changes the
  // operator does not need to be pulled across the room for.
  if (hint.signal !== "attention") return { eligible: false, reason: "not_attention" };

  // (3) A fresh queue response must still contain the alert. String comparison:
  // these ids are SQL bigints and must never round-trip through a JS number.
  const row = rows.find((candidate) => String(candidate.id) === hint.alertId);
  if (!row) return { eligible: false, reason: "not_in_queue_page" };

  // (4) Still pending. An alert someone else already closed between the hint and
  // the re-read is finished work — ringing for it sends an operator to a
  // decision that no longer exists.
  if (row.status !== AlertStatus.Pending) return { eligible: false, reason: "not_pending" };

  // (5) Same review cycle. A hint from an older cycle describes a decision round
  // that has already been superseded.
  if (row.review_cycle !== hint.reviewCycle) {
    return { eligible: false, reason: "stale_review_cycle" };
  }

  // (6) Matches the active scope filters. An operator who has narrowed to one
  // machine has said what they are responsible for; sound must respect it.
  const machineMatches = matches(
    filters.machineIds,
    hint.machineId != null ? Number(hint.machineId) : row.machine_id ?? null,
  );
  if (!machineMatches) return { eligible: false, reason: "filtered_out" };
  if (!matches(filters.deviceMxids, row.device_mxid)) {
    return { eligible: false, reason: "filtered_out" };
  }
  if (!matches(filters.types, row.type)) return { eligible: false, reason: "filtered_out" };

  return { eligible: true };
};

/** Convenience predicate for callers that do not need the refusal reason. */
export const isSoundEligible = (
  hint: AlertLiveHint,
  queueRows: SoundCandidateRow[],
  filters?: SoundFilters,
): boolean => soundVerdict(hint, queueRows, filters).eligible;

/**
 * Reduce a debounced BATCH of hints to the alert ids that may sound.
 *
 * The ticket caps sound at one per batch; returning the eligible ids rather than
 * a boolean lets the caller arm the alarm for exactly those alerts (so the badge
 * and the sound describe the same set) while the alarm itself — which is a
 * single repeating pattern — remains one sound regardless of how many qualified.
 */
export const eligibleAlertIds = (
  hints: AlertLiveHint[],
  queueRows: SoundCandidateRow[],
  filters?: SoundFilters,
): number[] =>
  hints
    .filter((hint) => isSoundEligible(hint, queueRows, filters))
    .map((hint) => Number(hint.alertId))
    .filter((id) => Number.isFinite(id));
